import { ServerError } from '@apollo/client/errors';
import { ErrorLink } from '@apollo/client/link/error';
import { EMPTY } from 'rxjs';

import DatadogLogger from 'components/core/logging/DatadogLogger';
import LoggingService from 'components/core/logging/LoggingService';
import { DatadogCustomAction } from 'enums/datadogCustomAction';
import { getOperationData, isNetworkStatusCodeIgnored } from 'utils/apolloUtils';
import { authManager, isLoggedIn } from 'utils/authUtils';

/**
 * Check if a 401 error is actually an authorization error (scope/permission issue)
 * rather than an authentication error (invalid/missing credentials)
 *
 * Per industry standard:
 * - 401 = Authentication failure (invalid/missing credentials) → should logout
 * - 403 = Authorization failure (valid credentials, insufficient permissions) → should NOT logout
 *
 * The backend incorrectly returns 401 for scope errors, so we detect them here.
 *
 * @see edealer-graphql backend error messages:
 * - UserPrincipal.java throws InvalidAuthorizationTarget with message:
 *   "X scope could not be authorized because the X does not exist"
 * - GlobalExceptionHandler.java line 126-136 returns HTTP 401 for InvalidAuthorizationTarget
 *   (should be 403/404 per HTTP standards)
 */
export const isAuthorizationError = (graphQLErrors?: readonly unknown[]): boolean => {
  if (!graphQLErrors || graphQLErrors.length === 0) {
    return false;
  }

  return graphQLErrors.some((error: unknown) => {
    const errorObject = error as { extensions?: { code?: string; debug?: string }; message?: string };
    const debug = errorObject?.extensions?.debug;
    const message = errorObject?.message;

    const scopePattern = /\b(scope|rooftop|group|website|white\s*label)\b/i;

    if (
      (typeof debug === 'string' && scopePattern.test(debug)) ||
      (typeof message === 'string' && scopePattern.test(message))
    ) {
      return true;
    }

    return errorObject?.extensions?.code === 'FORBIDDEN';
  });
};

/**
 * Extract the GraphQL `errors` array from a `ServerError`'s raw response body.
 *
 * Apollo v4 surfaces a non-2xx GraphQL response as a `ServerError` whose only body
 * representation is `bodyText` (a raw JSON string) — unlike v3, the error callback no
 * longer receives a parsed `graphQLErrors` array for network errors. The backend returns
 * HTTP 401 for scope/authorization failures with the details in that body, so we parse it
 * out to let `isAuthorizationError` distinguish scope errors (don't logout) from true
 * authentication failures (logout). Returns `undefined` for empty or non-JSON bodies, which
 * `isAuthorizationError` treats as a true authentication failure.
 */
const getServerErrorGraphQLErrors = (error: ServerError): readonly unknown[] | undefined => {
  try {
    const body = JSON.parse(error.bodyText) as { errors?: readonly unknown[] };

    return body.errors;
  } catch {
    return undefined;
  }
};

/**
 * Centralized error capturing following industry best practices:
 * - Logout only on true authentication failures (401 without authorization context)
 * - Handle authorization failures (403 or 401 with scope errors) gracefully
 * - Display error dialog with message upon 5xx
 *
 * In Apollo v4, the ErrorLink callback receives a single `error: ErrorLike` instead of
 * separate `graphQLErrors` and `networkError`. A non-2xx response is a `ServerError`; its
 * GraphQL errors live in the raw `bodyText`, which `getServerErrorGraphQLErrors` parses.
 */
export const errorLink = new ErrorLink(({ error, operation }) => {
  if (!ServerError.is(error)) {
    return;
  }

  const statusCode = error.statusCode;
  const graphQLErrors = getServerErrorGraphQLErrors(error);

  /**
   * Provide Datadog with request/response data for this networkError
   */
  const trackActions = () => {
    const request = getOperationData(operation);
    const response = {
      error,
      name: error.name,
      statusCode,
    };
    DatadogLogger.trackAction(DatadogCustomAction.APOLLO_ERROR_REQUEST, request);
    DatadogLogger.trackAction(DatadogCustomAction.APOLLO_ERROR_RESPONSE, response);
  };

  trackActions();

  if (statusCode === 401 && isLoggedIn()) {
    if (isAuthorizationError(graphQLErrors)) {
      LoggingService.log({
        error,
        message: 'ApolloClient: Authorization error (insufficient permissions)',
        messageContext: { graphQLErrors, operation: operation.operationName },
      });

      return;
    }

    void authManager.logout();
    LoggingService.clearUser();
  } else if (statusCode === 403 && isLoggedIn()) {
    LoggingService.log({
      error,
      message: 'ApolloClient: Forbidden (insufficient permissions)',
      messageContext: { graphQLErrors, operation: operation.operationName },
    });

    return;
  } else if (isNetworkStatusCodeIgnored(statusCode)) {
    LoggingService.log({
      error,
      message: 'ApolloClient: Expected ignored status code error',
      messageContext: { graphQLErrors },
    });

    return EMPTY;
  }
});
